import { create } from 'zustand';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase/app';
import type { AppConfig } from '../types/config';
import { CELL_PACKS } from '../config/iapConfig';

const DEFAULT_CONFIG: AppConfig = {
  batteryConsumption: [
    { radius: 1000, cost: 0 },
    { radius: 5000, cost: 50 },
    { radius: 10000, cost: 100 },
    { radius: 20000, cost: 200 },
    { radius: 50000, cost: 500 },
    { radius: 100000, cost: 1000 },
  ],
  batteryConsumptionIntervalSec: 10,
  messageSendCost: 10,
  dmSendCost: 1000,
  nicknameChangeCost: 1000,
  defaultBattery: 100_000,
  signupDefaultCells: 1_000_000,
  rewardAdAmount: 1000,
  autoChargeIntervalMinutes: 1440,
  autoChargeAmount: 100_000,
  manualFreqMode: {
    consumptionAmount: 100,
    jumpSendCost: 50,
  },
  locationJumpPermissions: {
    admin: true,
    operator: true,
    user: true,
  },
  mapLongPressPermissions: {
    admin: true,
    operator: true,
    user: false,
  },
  messageReception: {
    defaultMode: 'blur_beyond',
    defaultMaxRadius: 5000,
  },
  serverNotification: { enabled: true },
  nearbyUserNotification: {
    defaultIntervalMinutes: 5,
    enabled: true,
    showInChatEnabled: false,
  },
  dropBaseCost: 500,
  dropCostPerHour: 120000,
  dropCostPer5Min: 10000,
  dropCostPerKm: 200,
  dropMaxDurationHours: 72,
  dropMaxRadiusKm: 10,
  bannerPosition: {
    position: 'bottom',
    hideInRadioTab: false,
    collapsible: false,
  },
  showCellPurchaseButton: true,
  showRewardAdButton: true,
  cellPacks: CELL_PACKS,
  nearbyUsersLimit: 20,
  radarDisplayTimeMinutes: 15,
  radarValidationIntervalSeconds: 30,
  showBotBadge: true,
  deletionCooldownDays: 7,
  appActiveThresholdSeconds: 15,
  maxNotifiedMessageIds: 1000,
  maxSearchRadiusMeters: 50_000,
  maxActiveUsersTimeRangeMinutes: 60,
  mapConsentRequiredPlatforms: { web: true, android: true, ios: true },
};

interface ConfigState {
  config: AppConfig;
  loaded: boolean;

  /** Start real-time listener. Returns cleanup function. */
  subscribe: () => () => void;
}

export const useConfigStore = create<ConfigState>((set) => ({
  config: { ...DEFAULT_CONFIG },
  loaded: false,

  subscribe: () => {
    const unsub = onSnapshot(
      doc(db, 'appConfig', 'main'),
      (snap) => {
        if (snap.exists()) {
          set({ config: { ...DEFAULT_CONFIG, ...snap.data() } as AppConfig, loaded: true });
        } else {
          set({ config: { ...DEFAULT_CONFIG }, loaded: true });
        }
      },
      (err) => {
        if (err.code !== 'permission-denied') {
          console.error('[Config] Listener error:', err);
        }
        set({ loaded: true });
      },
    );
    return unsub;
  },
}));

export { DEFAULT_CONFIG };

/** Wait until config is loaded from Firestore (max 5s timeout) */
export function waitForConfig(): Promise<AppConfig> {
  const state = useConfigStore.getState();
  if (state.loaded) return Promise.resolve(state.config);
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      unsub();
      resolve(useConfigStore.getState().config);
    }, 5000);
    const unsub = useConfigStore.subscribe((s) => {
      if (s.loaded) {
        clearTimeout(timeout);
        unsub();
        resolve(s.config);
      }
    });
  });
}
